/**
 * OnboardingFlow — first-login guided tour.
 *
 * Shown once after a user first logs in. Role-aware:
 *   coach / manager  → 6-step team-management walkthrough
 *   player           → 4-step player walkthrough
 *
 * Completion sets user.onboardingCompleted = true via PUT /auth/profile.
 * Can also be triggered again from Profile settings.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../services/api.js';
import toast from 'react-hot-toast';

// ── Sport-aware emoji for welcome screen ─────────────────────────────────────
const SPORT_EMOJI = {
  hockey: '🏒', 'ball-hockey': '🏑', soccer: '⚽', baseball: '⚾',
  softball: '🥎', basketball: '🏀', football: '🏈', volleyball: '🏐',
  tennis: '🎾', badminton: '🏸', curling: '🥌', lacrosse: '🥍',
  rugby: '🏉', cricket: '🏏', other: '🏅',
};

// ── Thin progress bar ─────────────────────────────────────────────────────────
function ProgressBar({ step, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 2, transition: 'background 0.35s',
          background: i <= step ? 'var(--ice)' : 'var(--border)',
        }} />
      ))}
    </div>
  );
}

// ── Single step wrapper ───────────────────────────────────────────────────────
// Apr 2026 (Tom_Notes M1) — added `onBack` prop. When provided, the
// step renders a small back arrow at the top-left so users can
// retreat one step in any wizard. Doesn't render on step 0 (since
// "back" from the first step would exit the flow — the existing
// `onSkip` covers that case).
function Step({ emoji, title, subtitle, children, cta, onCta, ctaDisabled, secondary, onSecondary, onBack }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Back arrow — only when caller provides onBack. Anchored top-left
          so it doesn't compete with the centered hero. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to previous step"
          style={{
            alignSelf: 'flex-start',
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-2)', fontSize: 13, padding: '4px 8px',
            marginBottom: 8, marginLeft: -8,
            fontFamily: 'var(--font-body)',
          }}
          onMouseOver={e => e.currentTarget.style.color = 'var(--text)'}
          onMouseOut={e => e.currentTarget.style.color = 'var(--text-2)'}
        >
          ← Back
        </button>
      )}

      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 14 }}>{emoji}</div>
        <h2 style={{
          fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 26,
          letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text)',
          margin: '0 0 8px',
        }}>{title}</h2>
        {subtitle && (
          <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: 0 }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* Body content */}
      {children && (
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 20 }}>
          {children}
        </div>
      )}

      {/* CTAs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto', paddingTop: 8 }}>
        <button
          onClick={onCta}
          disabled={ctaDisabled}
          style={{
            width: '100%', padding: '14px 20px', borderRadius: 14, border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15,
            letterSpacing: '0.07em', textTransform: 'uppercase',
            background: ctaDisabled ? 'var(--surface-3)' : 'var(--ice)',
            color: ctaDisabled ? 'var(--text-2)' : '#000',
            transition: 'all 0.15s',
          }}>
          {cta}
        </button>
        {secondary && (
          <button onClick={onSecondary} style={{
            width: '100%', padding: '10px 20px', borderRadius: 12, border: '1px solid var(--border)',
            background: 'none', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}>
            {secondary}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Feature card row ──────────────────────────────────────────────────────────
function FeatureRow({ emoji, title, desc }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '12px 14px', borderRadius: 12, marginBottom: 8,
      background: 'var(--surface-2)', border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.3 }}>{emoji}</span>
      <div>
        <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', margin: '0 0 2px' }}>{title}</p>
        <p style={{ fontSize: 12, color: 'var(--text-2)', margin: 0, lineHeight: 1.5 }}>{desc}</p>
      </div>
    </div>
  );
}

// ── RSVP demo buttons ─────────────────────────────────────────────────────────
function RSVPDemo() {
  const [picked, setPicked] = useState(null);
  const opts = [
    { s: 'in',  label: "I'm In!", color: 'var(--goal)',    bg: 'rgba(0,255,136,0.12)', desc: 'You\'re playing!' },
    { s: 'out', label: "I'm Out", color: 'var(--penalty)', bg: 'rgba(255,68,68,0.12)', desc: 'Can\'t make it' },
  ];
  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10, textAlign: 'center' }}>
        Try it — tap a button:
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        {opts.map(o => (
          <button key={o.s} onClick={() => setPicked(o.s)} style={{
            flex: 1, padding: '12px 6px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: picked === o.s ? o.bg : 'var(--surface-2)',
            outline: picked === o.s ? `2px solid ${o.color}` : '2px solid transparent',
            fontWeight: 700, fontSize: 12, color: picked === o.s ? o.color : 'var(--text-2)',
            transition: 'all 0.15s',
          }}>{o.label}</button>
        ))}
      </div>
      {picked && (
        <p style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: 'var(--ice)', fontWeight: 600 }}>
          ✓ {opts.find(o => o.s === picked)?.desc} — your team gets notified instantly.
        </p>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Coach flow
// (Apr 30 2026 — was "Coach / Manager" before App_Change_List #1
// collapsed the staff roles into a single "Coach" surface in the UI.
// Legacy managers still hit this flow since their permissions are
// coach-equivalent on the backend.)
// ═════════════════════════════════════════════════════════════════════════════
function CoachFlow({ team, sport, onComplete, onSkip }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const sportEmoji = SPORT_EMOJI[sport] || '🏟';
  const total = 6;
  const next = () => setStep(s => s + 1);
  // Apr 2026 (Tom_Notes M1) — back navigation. Wizards previously
  // had no way to retreat once a step was past; coaches asked for
  // a back affordance to fix typos in earlier steps. Step 0 has no
  // "back" since there's nothing before it (use onSkip to exit).
  const back = () => setStep(s => Math.max(0, s - 1));

  const steps = [
    // 0 — Welcome
    <Step key="welcome"
      emoji={sportEmoji}
      title={`Welcome, Coach!`}
      subtitle={`You're managing ${team ? `"${team.name}"` : 'your team'} on Game Day. Let's get you set up in about 2 minutes.`}
      cta="Let's Go →"
      onCta={next}
      secondary="Skip tour"
      onSecondary={onSkip}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="👥" title="Player management" desc="Import players from screenshots, CSVs, or add them one by one." />
        <FeatureRow emoji="📅" title="Smart scheduling" desc="Auto-generate a full season or add games manually." />
        <FeatureRow emoji="⚖️" title="AI team balancing" desc="Balanced teams generated automatically when the player list locks." />
        <FeatureRow emoji="💳" title="Payment collection" desc="Collect spare fees via card, Apple Pay, or Google Pay." />
      </div>
    </Step>,

    // 1 — Add players
    <Step key="players"
      emoji="👥"
      title="Add Your Players"
      subtitle="Import an entire player list from a screenshot of your league site, a spreadsheet, or add players one at a time."
      cta="Go to Players →"
      onCta={() => { if (team) navigate(`/team/${team.id}/players`); next(); }}
      secondary="I'll do this later"
      onSecondary={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="📸" title="Screenshot import" desc="Take a photo of your league's player list — AI extracts names, emails, and phones automatically." />
        <FeatureRow emoji="📊" title="CSV / Excel import" desc="Upload a spreadsheet with player details for bulk importing." />
        <FeatureRow emoji="✉️" title="Invites" desc="Players without accounts get an invite email. They sign up and are added to your team automatically." />
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginTop: 8,
          background: 'var(--ice-a8)', border: '1px solid var(--ice-a20)',
        }}>
          <p style={{ fontSize: 12, color: 'var(--ice)', margin: 0, fontWeight: 600 }}>
            💡 Tip: You can import name-only players (no email yet) and add emails later — they'll still appear in your player list.
          </p>
        </div>
      </div>
    </Step>,

    // 2 — Schedule
    <Step key="schedule"
      emoji="📅"
      title="Schedule Your Games"
      subtitle="Create a season in seconds with Auto-Schedule, or add games one at a time."
      cta="Go to Schedule →"
      onCta={() => { if (team) navigate(`/team/${team.id}/games`); next(); }}
      secondary="I'll do this later"
      onSecondary={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="⚡" title="Auto-Schedule" desc="Pick your game day, time, and number of games — the full season is created in one tap." />
        <FeatureRow emoji="🔒" title="Player list lock" desc="Set an automatic lock window (e.g. 2 hours before game time). The player list freezes and teams can be balanced." />
        <FeatureRow emoji="📍" title="Arena changes" desc="Change the arena for a specific game — all confirmed players get an email notification." />
        <FeatureRow emoji="📥" title="Import from PDF" desc="Got an ice schedule PDF from your arena? Upload it and AI fills in all the dates and times." />
      </div>
    </Step>,

    // 3 — RSVP & player list
    <Step key="rsvp"
      emoji="✅"
      title="Availability & Players"
      subtitle="Players RSVP for each game. You see who's in, who's out, and who hasn't responded — and you can jab them."
      cta="Got It →"
      onCta={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="📊" title="Live attendance" desc="See a real-time count of confirmed players. Green = enough, red = short — you know at a glance." />
        <FeatureRow emoji="🤖" title="AI team balancing" desc="Once the player list locks, Game Day splits players into balanced teams using skill ratings." />
        <FeatureRow emoji="⭐" title="Rating players" desc="Rate each player 1–5 (Elite → Beginner). The AI uses these for balancing. You can also ask AI for a rating suggestion." />
        <FeatureRow emoji="🔔" title="Jab non-responders" desc="Tap 'Send Reminder' to send a push notification to everyone who hasn't RSVPed yet." />
      </div>
    </Step>,

    // 4 — Spare requests
    <Step key="spares"
      emoji="🙋"
      title="Spare Invites"
      subtitle="Short on players? Send a spare invite with one tap. Spares see your game and can join directly."
      cta="Got It →"
      onCta={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="📨" title="Instant spare blast" desc="Tap 'Invite Spares' on any game — all spare-classified players on your team get a notification." />
        <FeatureRow emoji="💳" title="Require payment" desc="Enable 'Require payment before spare is confirmed' in Team Settings. Spares pay via card, Apple Pay, or Google Pay when they RSVP." />
        <FeatureRow emoji="🥤" title="Drinks duty" desc="Track who brings drinks after the game. Game Day can auto-rotate the duty and send reminders." />
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginTop: 8,
          background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.2)',
        }}>
          <p style={{ fontSize: 12, color: 'var(--puck)', margin: 0, fontWeight: 600 }}>
            💡 Tip: Set a Stripe account in Team Settings to enable payment collection for spares.
          </p>
        </div>
      </div>
    </Step>,

    // 5 — Team settings
    <Step key="settings"
      emoji="⚙️"
      title="Team Settings"
      subtitle="Customize your team colour, sport, payment settings, and more — all in Team Settings."
      cta="Go to Team Settings →"
      onCta={() => { if (team) navigate(`/team/${team.id}`); onComplete(); }}
      secondary="I'm all set — finish tour"
      onSecondary={onComplete}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="🎨" title="Team colour" desc="Pick an accent colour — it's used throughout the app for your team." />
        <FeatureRow emoji="🏒" title="Sport" desc="Set your sport and avatar options will match (hockey, soccer, baseball, and more)." />
        <FeatureRow emoji="👤" title="Your role" desc="Set whether you're listed as a Coach in the player list." />
        <FeatureRow emoji="🔔" title="Weekly reminders" desc="Game Day can send weekly availability requests to all players automatically." />
      </div>
    </Step>,
  ];

  // Apr 2026 (Tom_Notes M1) — clone each step element with an
  // onBack prop EXCEPT the first one. Doing it here in one place
  // (rather than threading the prop through every Step instance
  // above) means future step additions automatically get back-
  // navigation without further wiring.
  const stepsWithBack = steps.map((s, i) =>
    i === 0 ? s : React.cloneElement(s, { onBack: back })
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <ProgressBar step={step} total={total} />
      {stepsWithBack[step]}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Player flow
// ═════════════════════════════════════════════════════════════════════════════
const POSITION_OPTIONS = [
  { key: 'forward',  label: 'Forward',  emoji: '🏒', desc: 'Centre, left wing, right wing' },
  { key: 'defense',  label: 'Defence',  emoji: '🛡️', desc: 'Defensive pair / blueliner' },
  { key: 'goalie',   label: 'Goalie',   emoji: '🥅', desc: 'Between the pipes' },
];

function PositionPicker({ selected, onChange }) {
  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter(k => k !== key)
      : [...selected, key];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
      {POSITION_OPTIONS.map(opt => {
        const active = selected.includes(opt.key);
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => toggle(opt.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
              background: active ? 'rgba(0,212,255,0.12)' : 'var(--surface-2)',
              border: `2px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
              transition: 'all 0.15s',
              WebkitTapHighlightColor: 'transparent',
            }}>
            <span style={{ fontSize: 26 }}>{opt.emoji}</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: active ? 'var(--ice)' : 'var(--text)' }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1 }}>{opt.desc}</div>
            </div>
            <div style={{
              width: 22, height: 22, borderRadius: 11, flexShrink: 0,
              background: active ? 'var(--ice)' : 'var(--surface-3)',
              border: `2px solid ${active ? 'var(--ice)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 900, color: '#000',
            }}>
              {active ? '✓' : ''}
            </div>
          </button>
        );
      })}
      {selected.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center', marginTop: 4 }}>
          Select at least one position (you can pick multiple)
        </p>
      )}
    </div>
  );
}

// Per-team classification picker shown in onboarding. Pre-fills with
// the coach's pre-assignment so the most common case ("just keep what
// the coach picked") is one tap. New joiners can override if their
// actual role differs — e.g., the coach's shareable invite link
// defaulted everyone to FT but this user is actually a spare.
const CLASS_OPTIONS = [
  { key: 'player_full',  label: 'Full-Timer',     desc: 'Regular player spot', emoji: '👤', isPrimary: true,  position: 'flex'   },
  { key: 'player_spare', label: 'Spare',          desc: 'Fill in when needed', emoji: '👥', isPrimary: false, position: 'flex'   },
  { key: 'goalie_full',  label: 'Goalie (Full-Timer)', desc: 'Regular goalie',  emoji: '🥅', isPrimary: true,  position: 'goalie' },
  { key: 'goalie_spare', label: 'Goalie (Spare)', desc: 'Backup goalie',       emoji: '🥅', isPrimary: false, position: 'goalie' },
];

function classificationKey(membership) {
  if (!membership) return 'player_full';
  if (membership.position === 'goalie') {
    return membership.isPrimary ? 'goalie_full' : 'goalie_spare';
  }
  return membership.isPrimary ? 'player_full' : 'player_spare';
}

function classificationLabel(team) {
  const k = classificationKey({
    isPrimary: team.my_is_primary !== false,
    position:  team.my_position === 'goalie' ? 'goalie' : 'flex',
  });
  return CLASS_OPTIONS.find(o => o.key === k)?.label || 'Full-Timer';
}

function ClassificationPicker({ teams, memberships, onChange }) {
  const setForTeam = (teamId, optKey) => {
    const opt = CLASS_OPTIONS.find(o => o.key === optKey);
    if (!opt) return;
    onChange({
      ...memberships,
      [teamId]: { isPrimary: opt.isPrimary, position: opt.position },
    });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
      {teams.map(team => {
        const current = classificationKey(memberships[team.id]);
        const coachPick = classificationLabel(team);
        return (
          <div key={team.id} style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            padding: 14,
          }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                {team.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                Your coach added you as <span style={{ color: 'var(--ice)', fontWeight: 600 }}>{coachPick}</span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {CLASS_OPTIONS.map(opt => {
                const active = current === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setForTeam(team.id, opt.key)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '10px 11px', borderRadius: 10, cursor: 'pointer',
                      background: active ? 'rgba(0,212,255,0.12)' : 'var(--surface-3)',
                      border: `2px solid ${active ? 'var(--ice-a40)' : 'transparent'}`,
                      transition: 'all 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                      textAlign: 'left',
                    }}>
                    <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{opt.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: active ? 'var(--ice)' : 'var(--text)', lineHeight: 1.2 }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.3 }}>
                        {opt.desc}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PlayerFlow({ team, sport, onComplete, onSkip, teams = [] }) {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [positions, setPositions] = useState([]);
  // 2026-07-10 (batch-B C3, per Gilles) — the notifications step is now
  // INTERACTIVE: the player sets email / push / SMS preferences right in
  // the tour instead of being told to visit their Profile later. SMS only
  // shows when at least one of their teams has SMS enabled (paid coach
  // plan; team_sms_settings.is_enabled surfaced as sms_enabled on the
  // teams list). Defaults mirror the DB defaults (email+push on).
  const [notifPrefs, setNotifPrefs] = useState({
    email: user?.notificationPrefs?.email !== false,
    push:  user?.notificationPrefs?.push  !== false,
    sms:   user?.notificationPrefs?.sms === true,
  });
  const smsAvailable = (teams || []).some(t => t.sms_enabled === true);
  const saveNotifPrefs = async () => {
    try {
      // MERGE into the existing prefs: PUT /auth/profile REPLACES the
      // whole notification_prefs JSONB with the (whitelisted) payload,
      // so we must carry the user's other keys (reminders, chat, …).
      const merged = { ...(user?.notificationPrefs || {}), ...notifPrefs };
      await api.put('/auth/profile', { notificationPrefs: merged });
      updateUser({ notificationPrefs: merged });
    } catch { /* non-critical — don't block finishing the tour */ }
    onComplete();
  };
  // Per-team classification map { teamId → { isPrimary, position } }.
  // Pre-filled with whatever the coach assigned at invite time so the
  // user sees "your coach added you as Spare" with the option to keep
  // it or change. The user's choice wins on save.
  const [memberships, setMemberships] = useState(() => {
    const init = {};
    for (const t of teams) {
      // Player teams only — coaches/managers never hit this picker
      // (they own the team and shouldn't self-demote in onboarding).
      if (t.member_role === 'player') {
        init[t.id] = {
          isPrimary: t.my_is_primary !== false, // default to FT if null/undefined
          position:  t.my_position === 'goalie' ? 'goalie' : 'flex',
        };
      }
    }
    return init;
  });
  const sportEmoji = SPORT_EMOJI[sport] || '🏟';
  // Player teams only count toward the classification step. If user
  // has zero player memberships, skip the step entirely (e.g., they
  // registered without an invite or were added as a coach).
  const playerTeams = teams.filter(t => t.member_role === 'player');
  const hasClassificationStep = playerTeams.length > 0;
  const next = () => setStep(s => s + 1);
  // Apr 2026 (Tom_Notes M1) — back navigation for the player flow.
  const back = () => setStep(s => Math.max(0, s - 1));

  const savePositions = async () => {
    if (positions.length > 0) {
      try {
        await api.put('/auth/profile', { preferredPositions: positions });
        updateUser({ preferredPositions: positions });
      } catch { /* non-critical */ }
    }
    next();
  };

  // Save per-team classification choices, then advance. Each PUT is
  // best-effort — a failure for one team doesn't block the wizard.
  // (If the coach later sees an unexpected classification on a player
  // they can override; this is purely the player's preference signal.)
  const saveMemberships = async () => {
    const teamIds = Object.keys(memberships);
    await Promise.all(teamIds.map(async (teamId) => {
      const choice = memberships[teamId];
      const original = teams.find(t => t.id === teamId);
      // Apr 2026 correctness fix: if the coach pre-assigned a specific
      // skater position ('forward' or 'defense'), the wizard UI only
      // offers the 2-way 'skater vs goalie' toggle — it collapses
      // anything-not-goalie to 'flex' for display. Previously, toggling
      // anything in the wizard would send position:'flex' and silently
      // overwrite the coach's 'forward'/'defense' assignment. Now we
      // only send the position when:
      //   (a) the user picked goalie (always send),
      //   (b) the original position was null/flex/goalie (safe to send
      //       'flex' because no specific skater assignment to clobber),
      //   (c) the user's choice genuinely differs from the original.
      // Otherwise we send ONLY the isPrimary change and leave position
      // untouched on the backend (endpoint treats omitted keys as
      // unchanged).
      const origIsPrimary = original?.my_is_primary !== false;
      const rawOrigPos    = original?.my_position || null;
      const isSpecificOrig = rawOrigPos === 'forward' || rawOrigPos === 'defense';
      const origPosForCompare = rawOrigPos === 'goalie' ? 'goalie' : 'flex';
      const primaryChanged  = choice.isPrimary !== origIsPrimary;
      const positionChanged = choice.position !== origPosForCompare;
      if (!primaryChanged && !positionChanged) return; // no-op
      // Don't clobber a coach-assigned 'forward'/'defense' by sending
      // 'flex' unless the user explicitly chose goalie (which is a
      // real change).
      const shouldSendPosition =
        choice.position === 'goalie' ||
        (!isSpecificOrig && positionChanged);
      const body = {};
      if (primaryChanged)   body.isPrimary = choice.isPrimary;
      if (shouldSendPosition) body.position = choice.position;
      if (Object.keys(body).length === 0) return;
      try {
        await api.put(`/teams/${teamId}/my-membership`, body);
      } catch (err) {
        console.warn(`Membership update failed for team ${teamId}:`, err.message);
      }
    }));
    next();
  };

  const steps = [
    // 0 — Welcome
    // 2026-07-10 (coach request) — "Skip tour" removed from the PLAYER
    // welcome and CTA renamed to "Next": players must walk the tour so
    // the position-setup step can't be skipped (unset positions degrade
    // the balancer). The coach variant (above) keeps its skip.
    <Step key="welcome"
      emoji={sportEmoji}
      title="Welcome to Game Day!"
      subtitle={team ? `You've been added to "${team}". Here's everything you need to know.` : "Here's how to get the most out of Game Day."}
      cta="Next →"
      onCta={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="✅" title="RSVP for games" desc="Tell your team if you're in or out for each game — takes 2 seconds." />
        <FeatureRow emoji="📅" title="See the schedule" desc="Full game schedule, arena locations, and time until your next game." />
        <FeatureRow emoji="💬" title="Team chat" desc="Chat with your teammates in real time." />
        <FeatureRow emoji="📊" title="Stats & achievements" desc="Track your attendance, streaks, and unlock achievements." />
      </div>
    </Step>,

    // 1 — Position preference (NEW)
    // 2026-07-10 (3.5b audit) — the "Skip for now →" relabel let players
    // advance with ZERO positions selected (the exact bypass the welcome-
    // step skip removal was meant to close). CTA is now fixed-label and
    // DISABLED until at least one position is picked — there is no other
    // exit (no X, no backdrop click, no Escape handler in this component),
    // so position setup genuinely can't be skipped by players anymore.
    <Step key="position"
      emoji="🏒"
      title="What Position Do You Play?"
      subtitle="Select all that apply — some players can play multiple positions. Your coach uses this to build balanced teams."
      cta="Save & Continue →"
      ctaDisabled={positions.length === 0}
      onCta={savePositions}
    >
      <PositionPicker selected={positions} onChange={setPositions} />
    </Step>,

    // 1b — Classification confirmation (only if user has player memberships)
    // Lets the new joiner confirm or change the FT/spare/goalie status
    // their coach pre-assigned at invite time. Without this step, every
    // shareable-link joiner became a full-timer regardless of what they
    // actually were on the team — coaches had to chase down each new
    // player and reclassify manually.
    ...(hasClassificationStep ? [
      <Step key="classification"
        emoji="👥"
        title={playerTeams.length > 1 ? 'Your Team Roles' : 'Your Role on the Team'}
        subtitle={playerTeams.length > 1
          ? "Confirm or change how you're listed on each team. Full-timers get first dibs on game spots; spares fill in when needed."
          : "Confirm or change how you're listed. Full-timers get first dibs on game spots; spares fill in when needed."}
        cta="Save & Continue →"
        onCta={saveMemberships}
      >
        <ClassificationPicker
          teams={playerTeams}
          memberships={memberships}
          onChange={setMemberships}
        />
      </Step>,
    ] : []),

    // RSVP — interactive demo so new players can try the in/out tap before
    // their first real game. Includes a quick rundown of related features
    // (reminders, arena address, roster lock) that they'll encounter on
    // the game page.
    <Step key="rsvp"
      emoji="✅"
      title="RSVP for Games"
      subtitle="When a game is scheduled, you'll get a notification asking if you're playing. It's as simple as tapping one button."
      cta="Got It →"
      onCta={next}
    >
      <div style={{ marginTop: 4 }}>
        <RSVPDemo />
        <div style={{ marginTop: 14 }}>
          <FeatureRow emoji="🔔" title="Reminders" desc="Your coach can send a reminder if you haven't responded yet. You'll get a notification." />
          <FeatureRow emoji="📍" title="Arena details" desc="The game page shows the arena address with a link to open directions." />
          <FeatureRow emoji="🔒" title="Player list lock" desc="Once the player list locks before game time, your coach generates balanced teams from who's confirmed in." />
        </div>
      </div>
    </Step>,

    // Schedule & chat — overview of the main app sections so new players
    // know where to find things after the wizard closes.
    <Step key="features"
      emoji="📅"
      title="Schedule & Chat"
      subtitle="Everything about your team lives here — games, chat, and your personal stats."
      cta="Got It →"
      onCta={next}
    >
      <div style={{ marginTop: 4 }}>
        <FeatureRow emoji="📅" title="Schedule tab" desc="Tap Schedule to see all upcoming games. Tap any game for full details, arena map, and your balanced team assignment." />
        <FeatureRow emoji="💬" title="Chat tab" desc="Real-time team chat. Messages are end-to-end encrypted — only your team can see them." />
        <FeatureRow emoji="📊" title="Stats tab" desc="Your personal attendance rate, win/loss record, and unlocked achievements." />
        <FeatureRow emoji="👤" title="Profile" desc="Set your avatar, change your display name, manage notification preferences." />
      </div>
    </Step>,

    // Notifications + done
    // 2026-07-10 (batch-B C3, per Gilles) — INTERACTIVE now: the player
    // sets email/push/SMS right here (saved merged into
    // users.notification_prefs on finish — see saveNotifPrefs). The SMS
    // row only appears when one of their teams has SMS enabled (paid
    // coach plan, team_sms_settings.is_enabled → teams-list sms_enabled).
    <Step key="notifications"
      emoji="🔔"
      title="Stay in the Loop"
      subtitle="Choose how you want to hear about game reminders, spare requests, and team updates."
      cta="Finish Setup ✓"
      onCta={saveNotifPrefs}
      secondary="Skip for now"
      onSecondary={onComplete}
    >
      <div style={{ marginTop: 4 }}>
        {[
          { key: 'email', emoji: '📧', title: 'Email notifications', desc: 'Game reminders and player-list updates by email.' },
          { key: 'push',  emoji: '📲', title: 'Push notifications',  desc: 'Instant alerts for reminders, spare requests, and roster locks.' },
          ...(smsAvailable ? [{ key: 'sms', emoji: '💬', title: 'Text messages (SMS)', desc: 'Your team has SMS enabled — get the most urgent alerts by text.' }] : []),
        ].map(row => (
          <label key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, marginBottom: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
            <span style={{ fontSize: 20 }}>{row.emoji}</span>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{row.title}</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>{row.desc}</span>
            </span>
            <input
              type="checkbox"
              checked={notifPrefs[row.key] === true}
              onChange={e => setNotifPrefs(p => ({ ...p, [row.key]: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: 'var(--ice)', cursor: 'pointer' }}
            />
          </label>
        ))}
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginTop: 8,
          background: 'var(--ice-a8)', border: '1px solid var(--ice-a20)',
        }}>
          <p style={{ fontSize: 12, color: 'var(--ice)', margin: 0, fontWeight: 600 }}>
            💡 Fine-tune exactly what you receive any time in Profile → Notifications.
          </p>
        </div>
      </div>
    </Step>,
  ];

  // Total = the actual number of steps in this run, since the
  // classification step is conditionally included based on whether
  // the user has any player memberships.
  const total = steps.length;

  // Apr 2026 (Tom_Notes M1) — see CoachFlow above for rationale.
  const stepsWithBack = steps.map((s, i) =>
    i === 0 ? s : React.cloneElement(s, { onBack: back })
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <ProgressBar step={step} total={total} />
      {stepsWithBack[step]}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main overlay
// ═════════════════════════════════════════════════════════════════════════════
export default function OnboardingFlow({ teams = [], activeTeam = null }) {
  const { user, updateUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Show onboarding only if not yet completed and user data is loaded.
  // Also wait for teams to be loaded — without them, the classification
  // step would be silently skipped, depriving new joiners of the chance
  // to confirm or change their coach's pre-assignment. We use a longer
  // timeout (1500ms) on first mount as a fallback in case teams really
  // are empty (e.g., user registered without an invite). After that
  // window, we show the wizard regardless so the user isn't stuck on
  // a blank screen forever.
  useEffect(() => {
    if (user && user.onboardingCompleted === false) {
      // If teams have loaded (any length), show shortly. Otherwise wait
      // a bit longer for them, then show anyway. The wizard's
      // classification step gracefully no-ops on empty teams.
      const teamsReady = Array.isArray(teams);
      const delay = teamsReady && teams.length > 0 ? 600 : 1500;
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
  }, [user, teams.length]);

  const complete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await api.put('/auth/profile', { onboardingCompleted: true });
      updateUser({ onboardingCompleted: true });
    } catch {
      // Non-critical — don't block the user
    }
    setVisible(false);
    setCompleting(false);
  };

  if (!visible || !user) return null;

  // Determine user's highest role across teams.
  // GET /teams returns member_role directly on each team object (not myRole).
  const isCoach = teams.some(t =>
    t.member_role === 'coach' || t.member_role === 'manager'
  ) || user.role === 'admin';

  const sport = activeTeam?.settings?.team_sport || 'hockey';
  const teamName = activeTeam?.name || null;
  const teamObj  = activeTeam || null;

  // Detect mobile width — onboarding modal uses bottom-sheet UX on
  // narrow screens (familiar mobile pattern, easy to dismiss with a
  // swipe-down gesture in apps like iOS Photos / Messages) but centers
  // on desktop where the bottom-aligned + top-only-rounded modal looked
  // awkwardly cut off. The check is one-shot at mount; orientation
  // changes during the wizard are rare and re-aligning mid-step would
  // be jarring.
  const isMobileWidth = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex',
      alignItems: isMobileWidth ? 'flex-end' : 'center',
      justifyContent: 'center',
      padding: isMobileWidth ? 0 : 24,
      background: 'rgba(0,0,0,0.80)',
      animation: 'fadeIn 0.3s ease',
      overflowY: 'auto',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 540,
        margin: isMobileWidth ? 0 : 'auto',
        maxHeight: isMobileWidth ? '92dvh' : 'min(92dvh, 720px)',
        background: 'var(--surface)',
        // Bottom-sheet on mobile (rounded top, flush bottom) vs full
        // modal card on desktop (rounded all corners, free-floating).
        borderRadius: isMobileWidth ? '20px 20px 0 0' : 20,
        border: '1px solid var(--border)',
        borderBottom: isMobileWidth ? 'none' : '1px solid var(--border)',
        boxShadow: isMobileWidth
          ? '0 -8px 60px rgba(0,0,0,0.6), 0 0 0 1px var(--ice-a10)'
          : '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px var(--ice-a10)',
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 24px',
        overflowY: 'auto',
        animation: isMobileWidth
          ? 'slideUp 0.35s cubic-bezier(0.32,0.72,0,1)'
          : 'fadeIn 0.3s ease',
      }}>
        {isCoach ? (
          <CoachFlow
            team={teamObj}
            sport={sport}
            onComplete={complete}
            onSkip={complete}
          />
        ) : (
          <PlayerFlow
            team={teamName}
            sport={sport}
            teams={teams}
            onComplete={complete}
            onSkip={complete}
          />
        )}
      </div>
    </div>
  );
}
